import { call, fork, put, select, take, takeEvery } from "typed-redux-saga";
import ConfigurationClient from "../../web-apis/configuration-client";
import { MVisionAppClient } from "../configurationTarget/mvision-client-list";
import { ContouringCustomizationEntities, ContouringCustomizationEntitiesForExport, ContouringCustomizationOutput, ContouringCustomizationOutputEntities, ContouringRoi, EXPORT_CONTOUR_JSON_FILE_PREFIX, GlobalContouringRoi } from "./contouring-types";
import { allModelCustomizationsReset, contouringSelectors, customizationBaseDuplicated, customizationDuplicationItemsAdded, customizationOutputDuplicated, modelCustomizationExported, modelCustomizationExportFailed, modelCustomizationImported, modelCustomizationImportFailed, modelCustomizationObjectsSetAsModified, modelCustomizationOutputReplaced, modelCustomizationPdfExportRequested, modelCustomizationSaved, modelCustomizationsSet, outputDuplicationItemsAdded, pdfExportFinished, pdfExportStarted, resetAllModelCustomizationsFinished, resetAllModelCustomizationsStarted, resetSingleCustomizationOutputFinished, resetSingleCustomizationOutputStarted, saveModelCustomizationFinished, saveModelCustomizationStarted, singleModelCustomizationOutputReset } from "./contouringSlice";
import { BackendValidationError, getApiErrorMessage } from "../../util/errors";
import { hasBlockingFormValidationErrors } from "../global-types/store-errors";
import { SaveResult, buildSaveFinishResult } from "../../web-apis/save-response";
import { Action, Update } from "@reduxjs/toolkit";
import { appConfigSelectors } from "../appConfig/appConfigSlice";
import { DeploymentMode } from "../appConfig/appDeploymentInfoTypes";
import { downloadBlobToDisk, downloadFileToDisk } from "../../util/files";
import { convertContouringViewModelsToJson, createNewContouringCustomizationOutput, duplicateRoiCustomization } from "./contouring-helpers";
import { createNewModelCustomizationBase, duplicateModelCustomizationMetadataItem, generateNewId } from "../global-types/customization-helpers";
import { METADATA_FILENAME_ATTRIBUTE, ModelType, OutputMetadataItem } from "../global-types/customization-types";
import { ensureFilenameHasDcmExtension } from "../../pages/customization/file-operations";
import { modelCustomizationPageFocusSet } from "../appStatus/appStatusSlice";
import { callApi } from "../sagas";
import { DuplicatedIdMap } from "../global-types/reducer-helpers";


export function* fetchContouringCustomizationsSaga(appClient: MVisionAppClient | undefined) {

    const result = yield* callApi({
        doApiCall: function* (client: ConfigurationClient) {
            const contouringCustomizations = appClient === undefined ? null : yield* call(async () => client.fetchContouringCustomizationsAsync(appClient));
            if (contouringCustomizations !== null) {
                const fetchModels = yield* call(async () => client.fetchAvailableModelsAsync(appClient, ModelType.Contouring));
                if (fetchModels !== undefined) {
                    // if the available models collection is in use, mark which segmentation models are unavailable
                    for (const model of contouringCustomizations.models) {
                        model.isAvailable = fetchModels.availableModels.includes(model.modelName);
                    }
                } else {
                    throw new Error('Could not retrieve list of available contouring models');
                }
            }
            return contouringCustomizations;
        },
        onSuccess: function* (result: ContouringCustomizationEntities | null) {
            yield* put(modelCustomizationsSet({ customizations: result }));
        },
        onFailure: function* (error: Error | BackendValidationError) {
            yield* put(modelCustomizationsSet({ customizations: null, errorMessage: error.message || 'Unspecified error.' }));
        }
    });

    return result;
}

function* saveContouringCustomizationSaga(action: Action) {
    if (modelCustomizationSaved.match(action)) {
        const { configurationTarget, force } = action.payload;

        yield* callApi({
            doApiCall: function* (client: ConfigurationClient) {
                // collect model customization -- this is essentially a cache database dump (but global rois are not needed)
                const modelCustomization: ContouringCustomizationEntitiesForExport = yield* collectContouringCustomizationForExport();

                // sanity-check that everything is valid in case invalid data slips through the UI debouncing
                const formValidationErrors = yield* select(contouringSelectors.selectFormValidationErrors);
                if (hasBlockingFormValidationErrors(formValidationErrors)) {
                    throw new Error(`Contour+ model configuration has invalid entries. Please fix them before saving.`);
                }

                yield* put(saveModelCustomizationStarted());
                const result = yield* call(async () => client.saveContouringCustomizationAsync(configurationTarget, modelCustomization, force));
                return result;
            },
            onFinish: function* (result: SaveResult | undefined, error) {
                const r = buildSaveFinishResult(result, error, ModelType.Contouring);
                yield* put(saveModelCustomizationFinished({
                    saveWasSuccessful: r.success,
                    errorMessage: r.errorMessage,
                    error: r.validationError,
                    concurrentSaveError: r.concurrentSaveError,
                    updatedTimestamps: r.updatedTimestamps,
                }));
                return r.success;
            }
        });
    }
}

function* duplicateContourCustomizationSaga(action: Action) {
    if (customizationBaseDuplicated.match(action)) {
        const { customizationBaseId, newCustomizationName } = action.payload;

        const sourceCustomizationBase = yield* select(contouringSelectors.selectCustomizationBaseById, customizationBaseId);
        if (!sourceCustomizationBase) { throw new Error(`Could not retrieve customization base with id ${customizationBaseId}`); }
        const parentSegmentationModel = yield* select(contouringSelectors.selectModelById, sourceCustomizationBase.modelId);
        if (!parentSegmentationModel) { throw new Error(`Could not retrieve parent segmentation model with id ${sourceCustomizationBase.modelId}`); }

        const newCustomizationBaseId = generateNewId();

        // collect and map all ids we duplicate here so we can also duplicate related form errors to new targets
        const duplicatedIds: DuplicatedIdMap[] = [{ sourceId: customizationBaseId, targetId: newCustomizationBaseId }];

        // duplicate customization outputs (note: triggers are NOT duplicated)
        const newOutputs: ContouringCustomizationOutput[] = [];
        let newRois: ContouringRoi[] = [];
        let newMetadata: OutputMetadataItem[] = [];
        let globalRoiChanges: Update<GlobalContouringRoi, string>[] = [];
        for (const outputId of sourceCustomizationBase.outputs) {
            const sourceCustomizationOutput = yield* select(contouringSelectors.selectOutputById, outputId);
            if (!sourceCustomizationOutput) { throw new Error(`Could not retrieve customization output with id ${outputId}`); }
            const duplicationEntities = yield* duplicateCustomizationOutputFromStore(sourceCustomizationOutput, undefined, newCustomizationBaseId);
            newOutputs.push(duplicationEntities.duplicatedOutput);
            newRois = newRois.concat(duplicationEntities.newRois);
            newMetadata = newMetadata.concat(duplicationEntities.newMetadata);
            globalRoiChanges = globalRoiChanges.concat(duplicationEntities.globalRoiChanges);

            duplicatedIds.push(...duplicationEntities.duplicatedIds);
        }

        // duplicate customization base
        const duplicatedCustomizationBase = createNewModelCustomizationBase(
            newCustomizationName,
            sourceCustomizationBase.description,
            newOutputs.map(o => o.id),
            [],
            [],
            sourceCustomizationBase.modelId,
            newCustomizationBaseId,
            true
        );

        // store everything
        yield* put(customizationDuplicationItemsAdded({
            parentModelId: parentSegmentationModel.id,
            customization: duplicatedCustomizationBase,
            outputs: newOutputs,
            metadata: newMetadata,
            rois: newRois,
            globalRoiChanges: globalRoiChanges,
            duplicatedIds: duplicatedIds,
        }));

        // focus on the new duplicate
        if (newOutputs.length > 0) {
            yield* put(modelCustomizationPageFocusSet(newOutputs[0].id));
        }
    }
}

function* duplicateContourOutputSaga(action: Action) {
    if (customizationOutputDuplicated.match(action)) {
        const { customizationOutputId, newFilename } = action.payload;

        const sourceCustomizationOutput = yield* select(contouringSelectors.selectOutputById, customizationOutputId);
        if (!sourceCustomizationOutput) { throw new Error(`Could not retrieve customization output with id ${customizationOutputId}`); }
        const sourceCustomizationBase = yield* select(contouringSelectors.selectCustomizationBaseById, sourceCustomizationOutput.modelCustomizationBaseId);
        if (!sourceCustomizationBase) { throw new Error(`Could not retrieve customization base with id ${sourceCustomizationOutput.modelCustomizationBaseId}`); }

        const duplicationEntities = yield* duplicateCustomizationOutputFromStore(sourceCustomizationOutput, newFilename);

        // collect and map all ids we duplicate here so we can also duplicate related form errors to new targets
        const duplicatedIds: DuplicatedIdMap[] = [...duplicationEntities.duplicatedIds];

        // store everything
        yield* put(outputDuplicationItemsAdded({
            parentCustomizationId: sourceCustomizationBase.id,
            output: duplicationEntities.duplicatedOutput,
            metadata: duplicationEntities.newMetadata,
            rois: duplicationEntities.newRois,
            globalRoiChanges: duplicationEntities.globalRoiChanges,
            duplicatedIds
        }));

        // focus on the new duplicate
        yield* put(modelCustomizationPageFocusSet(duplicationEntities.duplicatedOutput.id));
    }
}

function* resetAllContouringCustomizationsSaga(action: Action) {
    if (allModelCustomizationsReset.match(action)) {
        const configurationTarget = action.payload;

        yield* callApi({
            doApiCall: function* (client) {
                yield* put(resetAllModelCustomizationsStarted());

                let success = yield* call(async () => client.deleteContouringCustomizationAsync(configurationTarget));
                if (success) {
                    // immediately fetch updated (reset) customizations
                    success = yield* call(fetchContouringCustomizationsSaga, configurationTarget);
                }

                return success;
            },
            onFinish: function* (result, error) {
                let errorMessage = null;
                const success = result || false;

                if (error !== null) {
                    console.error('Was not able to reset Contour+ customizations');
                    errorMessage = getApiErrorMessage(error);
                    console.error(errorMessage);
                } else if (!success) {
                    errorMessage = 'Unable to reset Contour+ customizations. There might be a problem with the server.';
                }


                yield* put(resetAllModelCustomizationsFinished({
                    resetWasSuccessful: success,
                    errorMessage: errorMessage || undefined
                }));

                return success;
            },
        });
    }
}

/** Resets a single Contour+ CUSTOMIZATION OUTPUT (not base) */
function* resetSingleContouringCustomizationOutputSaga(action: Action) {
    if (singleModelCustomizationOutputReset.match(action)) {

        const customizationOutputId = action.payload;

        const output = yield* select(contouringSelectors.selectOutputById, customizationOutputId);
        const modelName = yield* select(contouringSelectors.selectModelNameForCustomizationOutput, customizationOutputId);

        if (!output || !modelName) {
            throw new Error('No model name specified, cannot fetch default configuration for model');
        }

        yield* callApi({
            doApiCall: function* (client) {
                yield* put(resetSingleCustomizationOutputStarted());

                // get default config for this model
                return yield* call(async () => client.fetchDefaultConfigurationForContouringOutput(modelName));
            },
            onFinish: function* (result, error) {
                if (result) {
                    // replace existing output and its items with received default items
                    yield* put(modelCustomizationOutputReplaced({ customizationOutputId, newOutputEntities: result }));
                }

                let errorMessage = null;
                if (error !== null) {
                    console.error(`Was not able to reset single customization output (${customizationOutputId})`);
                    errorMessage = `An error occurred when trying to reset target file: ${getApiErrorMessage(error)}`;
                    console.error(errorMessage);
                }

                const success = error === null;
                yield* put(resetSingleCustomizationOutputFinished({
                    resetWasSuccessful: success,
                    errorMessage: errorMessage || undefined
                }));

                return success;
            }
        });
    }
}

function* exportContouringCustomization(appClient: MVisionAppClient | undefined) {
    try {
        // collect model customization -- this is essentially a cache database dump (but global rois are not needed)
        const modelCustomization: ContouringCustomizationEntitiesForExport = yield* collectContouringCustomizationForExport();
        const jsonObject = yield* call(async () => convertContouringViewModelsToJson(modelCustomization, true));

        // Default filename if appClient?.userName is undefined or empty
        const defaultFilename = "model-customizations.json";
        const localModeFilename = "local.json";
        const appDeploymentInfo = yield* select(appConfigSelectors.selectAppDeploymentInfo);
        const isLocalMode = appDeploymentInfo && (appDeploymentInfo.deploymentMode === DeploymentMode.Cockpit || appDeploymentInfo.deploymentMode === DeploymentMode.Local);
        const filename = `${EXPORT_CONTOUR_JSON_FILE_PREFIX}${isLocalMode ? localModeFilename : (appClient?.userName ? `${appClient.userName}.json` : defaultFilename)}`;

        downloadFileToDisk(JSON.stringify(jsonObject, null, 2), filename, 'application/json');
        yield* put(modelCustomizationExportFailed(null)); // resets any failure error message
    } catch (error) {
        console.log(`An error occurred in exportContouringCustomization:`);
        console.error(error);

        const errorMessage = getApiErrorMessage(error, 'Contour+ JSON export failed.');
        yield* put(modelCustomizationExportFailed(errorMessage));
    }
}

function* importContouringCustomization(modelCustomization: ContouringCustomizationEntities) {
    try {
        yield* put(modelCustomizationsSet({ customizations: modelCustomization, retainOriginalData: true }));
        yield* put(modelCustomizationObjectsSetAsModified(modelCustomization)); // set the entire ModelCustomizationEntities object as modified
        yield* put(modelCustomizationImportFailed(null));  // resets any failure error message
    } catch (error) {
        const errorMessage = getApiErrorMessage(error, 'Contour+ JSON import failed.');
        yield* put(modelCustomizationImportFailed(errorMessage));
    }
}




/// helper functions for contouring config sagas

export function* collectContouringCustomizationForExport() {
    const modelCustomization: ContouringCustomizationEntitiesForExport = {
        models: yield* select(contouringSelectors.selectModels),
        customizationBases: yield* select(contouringSelectors.selectCustomizationBases),
        contouringOutputs: yield* select(contouringSelectors.selectOutputs),
        contouringRois: yield* select(contouringSelectors.selectRois),
        outputMetadata: yield* select(contouringSelectors.selectOutputMetadata),
        aeTitleRules: yield* select(contouringSelectors.selectAeTitleRules),
        dicomRules: yield* select(contouringSelectors.selectDicomRules),
        dicomAttributeRules: yield* select(contouringSelectors.selectDicomAttributeRules),
    };

    return modelCustomization;
}

type DuplicatedOutputEntities = {
    duplicatedOutput: ContouringCustomizationOutput;
    newRois: ContouringRoi[];
    newMetadata: OutputMetadataItem[];
    globalRoiChanges: Update<GlobalContouringRoi, string>[];
    duplicatedIds: DuplicatedIdMap[];
};

/**
 * Duplicates a customization output from store.
 * 
 * This should only be called from within related sagas (or from unit tests).
 * 
 */
function* duplicateCustomizationOutputFromStore(
    sourceCustomizationOutput: ContouringCustomizationOutput,
    newFilename?: string,
    newCustomizationBaseId?: string) {

    const newRois: ContouringRoi[] = [];
    const newMetadata: OutputMetadataItem[] = [];
    const globalRoiChanges: Update<GlobalContouringRoi, string>[] = [];
    const newCustomizationOutputId = generateNewId();
    const duplicatedIds: DuplicatedIdMap[] = [{ sourceId: sourceCustomizationOutput.id, targetId: newCustomizationOutputId }];

    let duplicateFilename: string = '';

    // duplicate metadata
    for (const metadataId of sourceCustomizationOutput.metadata) {
        const metadata = yield* select(contouringSelectors.selectOutputMetadataById, metadataId);
        if (!metadata) { throw new Error(`Could not find customization metadata with id ${metadataId}`) }
        const duplicatedMetadata = duplicateModelCustomizationMetadataItem(metadata, newCustomizationOutputId);

        // handle filename metadata
        if (duplicatedMetadata.attribute === METADATA_FILENAME_ATTRIBUTE && newFilename !== undefined) {
            duplicatedMetadata.value = ensureFilenameHasDcmExtension(newFilename);
        }

        if (duplicatedMetadata.attribute === METADATA_FILENAME_ATTRIBUTE) {
            duplicateFilename = duplicatedMetadata.value;
        }

        newMetadata.push(duplicatedMetadata);
        duplicatedIds.push({ sourceId: metadataId, targetId: duplicatedMetadata.id });
    }

    // duplicate rois
    for (const roiId of sourceCustomizationOutput.rois) {
        const roi = yield* select(contouringSelectors.selectRoiById, roiId);
        if (!roi) { throw new Error(`Could not find roi with id ${roiId}`); }
        const duplicatedRoi = duplicateRoiCustomization(roi, newCustomizationOutputId);
        newRois.push(duplicatedRoi);
        duplicatedIds.push({ sourceId: roiId, targetId: duplicatedRoi.id });
    }

    // create new output
    const roiIds = newRois.map(r => r.id);
    const metadataIds = newMetadata.map(m => m.id);
    const duplicatedOutput = createNewContouringCustomizationOutput(metadataIds, roiIds, newCustomizationBaseId || sourceCustomizationOutput.modelCustomizationBaseId, duplicateFilename, newCustomizationOutputId, true);

    // update global rois -- don't recalculate globals
    for (const newRoi of newRois) {
        const globalRoiId = newRoi.globalRoiId;
        if (globalRoiId) {
            // if original roi matched a global roi, then the duplicate should also
            const globalRoi = yield* select(contouringSelectors.selectGlobalRoiById, globalRoiId);
            if (!globalRoi) { throw new Error(`Could not find global roi with id ${globalRoiId}`); }
            globalRoiChanges.push({ id: globalRoiId, changes: { coveredRois: globalRoi.coveredRois.concat(newRoi.id) } });
        } else {
            // if the original roi did not match a global roi, then find the matching entry and put the duplicate also into excluded rois
            const globalRois = yield* select(contouringSelectors.selectGlobalRois);
            const globalRoi = globalRois.find(g => g.operation === newRoi.operation);
            if (globalRoi) {
                globalRoiChanges.push({ id: globalRoi.id, changes: { excludedRois: globalRoi.excludedRois.concat(newRoi.id) } });
            }
        }
    }

    const result: DuplicatedOutputEntities = {
        duplicatedOutput,
        newMetadata,
        newRois,
        globalRoiChanges,
        duplicatedIds,
    };

    return result;
};




/// watches for contouring config sagas

export function* watchSaveContouringCustomizationSaga() {
    // take all contouring customization save requests as they come -- do limiting simultaenous saves by temporarily
    // disabling relevant ui elements instead of doing it with sagas here
    yield* takeEvery(modelCustomizationSaved, saveContouringCustomizationSaga);
}

export function* watchContourCustomizationDuplicatedSaga() {
    yield* takeEvery(customizationBaseDuplicated, duplicateContourCustomizationSaga);
}

export function* watchContourOutputDuplicatedSaga() {
    yield* takeEvery(customizationOutputDuplicated, duplicateContourOutputSaga);
}

function* watchResetAllContouringCustomizationsSaga() {
    yield* takeEvery(allModelCustomizationsReset, resetAllContouringCustomizationsSaga);
}

function* watchResetSingleContouringCustomizationOutputSaga() {
    yield* takeEvery(singleModelCustomizationOutputReset, resetSingleContouringCustomizationOutputSaga);
}

function* exportContouringCustomizationPdf(appClient: MVisionAppClient) {
    try {
        yield* callApi({
            doApiCall: function* (client: ConfigurationClient) {
                const blob: Blob = yield* call(async () => client.exportCustomizationPdfAsync(appClient, ModelType.Contouring));
                return blob;
            },
            onFinish: function* (result: Blob | undefined, error) {
                if (error || !result) {
                    const errorMessage = getApiErrorMessage(error, 'Contour+ PDF export failed.');
                    yield* put(modelCustomizationExportFailed(errorMessage));
                    yield* put(pdfExportFinished());
                    return false;
                }

                const appDeploymentInfo = yield* select(appConfigSelectors.selectAppDeploymentInfo);
                const isLocalMode = appDeploymentInfo && (appDeploymentInfo.deploymentMode === DeploymentMode.Cockpit || appDeploymentInfo.deploymentMode === DeploymentMode.Local);
                const filename = `${EXPORT_CONTOUR_JSON_FILE_PREFIX}${isLocalMode ? 'local' : (appClient.userName || 'customizations')}.pdf`;

                downloadBlobToDisk(result, filename);

                yield* put(modelCustomizationExportFailed(null));
                yield* put(pdfExportFinished());
                return true;
            },
        });
    } catch (error) {
        console.error('An error occurred in exportContouringCustomizationPdf:', error);
        const errorMessage = getApiErrorMessage(error, 'Contour+ PDF export failed.');
        yield* put(modelCustomizationExportFailed(errorMessage));
        yield* put(pdfExportFinished());
    }
}

export function* watchExportContouringCustomizationPdfSaga() {
    while (true) {
        const action = yield* take(modelCustomizationPdfExportRequested);
        yield* put(pdfExportStarted());
        if (!action.payload) {
            yield* put(modelCustomizationExportFailed('PDF export of contouring customization has failed.'));
            yield* put(pdfExportFinished());
            continue;
        }
        yield* fork(exportContouringCustomizationPdf, action.payload);
    }
}

function* watchExportContouringCustomizationSaga() {
    while (true) {
        const action = yield* take(modelCustomizationExported);
        const errorMessage = 'Export of contouring customization has failed.';
        if (!action.payload) {
            yield* put(modelCustomizationExportFailed(errorMessage));
            continue;
        }
        yield* fork(exportContouringCustomization, action.payload);
    }
}

export function* watchImportContouringCustomizationSaga() {
    while (true) {
        const action = yield* take(modelCustomizationImported);
        const errorMessage = 'No JSON data provided for contouring import.';

        if (!action.payload) {
            yield* put(modelCustomizationImportFailed(errorMessage));
            continue;
        }

        yield* fork(importContouringCustomization, action.payload);
    }
}

/** Returns all relevant watches to be added to a main root watch saga */
export function getWatchesForContouringConfigSagas() {
    return [
        watchSaveContouringCustomizationSaga(),
        watchContourCustomizationDuplicatedSaga(),
        watchContourOutputDuplicatedSaga(),
        watchResetAllContouringCustomizationsSaga(),
        watchResetSingleContouringCustomizationOutputSaga(),
        watchExportContouringCustomizationSaga(),
        watchExportContouringCustomizationPdfSaga(),
        watchImportContouringCustomizationSaga(),
    ];
}
